/**
 * UI Helper utility functions for common UI operations
 */

/**
 * Get avatar color based on name hash
 * Generates a consistent color for a given name
 * 
 * @param name - The name to generate color for
 * @returns Hex color code
 */
export function getAvatarColor(name: string): string {
  if (!name) return '#9CA3AF' // Default gray color
  
  const colors = [
    '#4CAF50', // Green
    '#2196F3', // Blue
    '#FF9800', // Orange
    '#9C27B0', // Purple
    '#F44336', // Red
    '#00BCD4', // Cyan
    '#FF5722', // Deep Orange
    '#8BC34A', // Light Green
    '#3F51B5', // Indigo
    '#E91E63'  // Pink
  ]
  
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

/**
 * Get initials from full name
 * Extracts and formats initials from a name (up to 2 characters)
 * 
 * @param name - The full name
 * @returns Uppercase initials (max 2 characters)
 */
export function getInitials(name: string): string {
  if (!name) return '?'
  
  return name
    .split(' ')
    .filter(part => part.length > 0)
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Get CSS class for progress bar based on percentage
 * Returns appropriate styling class based on progress value
 * 
 * @param progress - Progress percentage (0-100)
 * @returns CSS class name
 */
export function getProgressClass(progress: number): string {
  if (progress >= 100) return 'progress-success'
  if (progress >= 65) return 'progress-warning'
  if (progress > 0) return 'progress-info'
  return 'progress-default'
}

/**
 * Get status color based on status value
 * Returns appropriate color for various status types
 * 
 * @param status - Status string
 * @returns Hex color code
 */
export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    'COMPLETED': '#4CAF50',
    'DONE': '#4CAF50',
    'IN_PROGRESS': '#2196F3',
    'PENDING': '#FF9800',
    'TODO': '#FF9800',
    'CANCELLED': '#F44336',
    'ON_HOLD': '#9C27B0',
    'ACTIVE': '#4CAF50',
    'INACTIVE': '#9E9E9E',
    'SUSPENDED': '#F44336'
  }
  
  return statusColors[status?.toUpperCase()] || '#9E9E9E'
}

/**
 * Get badge variant based on status
 * Returns appropriate badge style variant
 * 
 * @param status - Status string
 * @returns Badge variant name
 */
export function getBadgeVariant(status: string): string {
  const variants: Record<string, string> = {
    'COMPLETED': 'success',
    'DONE': 'success',
    'IN_PROGRESS': 'primary',
    'PENDING': 'warning',
    'TODO': 'warning',
    'CANCELLED': 'danger',
    'ON_HOLD': 'secondary',
    'ACTIVE': 'success',
    'INACTIVE': 'secondary'
  }
  
  return variants[status?.toUpperCase()] || 'default'
}
